import { Badge, Divider, Heading, Icon, Pressable, Text } from '@ui/themed';
import { t } from 'i18next';
import {
  Children,
  forwardRef,
  isValidElement,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from 'react';
import { cn, SHIMMER_CLASSES } from '../../../lib/styles';
import type { IconName } from '../icon/types';
import type { AccordionItemProps } from './types';

const ACCORDION_TRANSITION_MS = 400;

/**
 * Flush stack of rows. The platform `Accordion` owns the single outer frame
 * (border/surface/radius); rows sit edge-to-edge and are separated by a
 * platform `Divider` hairline rather than floating apart with a gap.
 */
export function AccordionItems({ children }: { children: ReactNode }) {
  const items = Children.toArray(children).filter(isValidElement);

  return (
    <div className="flex w-full flex-col">
      {items.flatMap((child, index) => [
        index > 0 && <Divider key={`divider-${child.key ?? index}`} />,
        child,
      ])}
    </div>
  );
}

/**
 * Item internals — header trigger and animated body. Rows are frameless; the outer
 * frame is owned by each platform `Accordion`. Forwards a ref to the row root so
 * consumers (e.g. the checklist) can anchor to it.
 */
export const AccordionItemContent = forwardRef<HTMLDivElement, AccordionItemProps>(
  function AccordionItemContent(props, ref) {
    const bodyId = useId();
    const { isExpanded, onExpandedChange } = props;
    // Both disabled states are inert; `'locked'` additionally swaps the chevron
    // for a padlock to signal the row can't be opened yet.
    const inert = Boolean(props.disabled);
    const locked = props.disabled === 'locked';

    return (
      <div ref={ref} className={cn('flex w-full flex-col', inert && 'opacity-60')}>
        <h3 className="h-fit w-full">
          <Pressable
            fullWidth
            type="flush"
            disabled={inert}
            ariaExpanded={isExpanded}
            ariaControls={bodyId}
            onClick={() => onExpandedChange(!isExpanded)}
          >
            <span className={cn('flex size-full items-center justify-between gap-3 px-4 py-3')}>
              <span
                className={cn('flex min-w-0 items-center', props.subtitle ? 'gap-3' : 'gap-1.5')}
              >
                {props.icon &&
                  (typeof props.icon === 'string' ? (
                    <Icon name={props.icon as IconName} size="md" />
                  ) : (
                    props.icon
                  ))}
                <span className="flex min-w-0 flex-col gap-0.5 text-start">
                  <Heading level="span" style="sm">
                    {props.title}
                  </Heading>
                  {props.subtitle && <Text variant="secondary">{props.subtitle}</Text>}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {props.badge && (
                  <Badge variant={props.badge.variant} size="sm" icon={props.badge.icon}>
                    {props.badge.text}
                  </Badge>
                )}
                {locked ? (
                  <Icon name="lock" size="md" />
                ) : (
                  <span
                    style={{ transitionDuration: `${ACCORDION_TRANSITION_MS}ms` }}
                    className={cn(
                      'transition-transform ease-out',
                      isExpanded && 'rotate-180 rtl:-rotate-180',
                    )}
                  >
                    <Icon name="chevronDown" size="md" />
                  </span>
                )}
              </span>
            </span>
          </Pressable>
        </h3>
        <AccordionBody id={bodyId} isOpen={isExpanded}>
          {props.children}
        </AccordionBody>
      </div>
    );
  },
);

// Animates both directions via a grid-rows transition (0fr <-> 1fr). The body is
// unmounted while fully closed (keeping collapsed content out of the a11y tree)
// and lingers through the close transition before unmounting.
function AccordionBody({
  id,
  isOpen,
  children,
}: PropsWithChildren<{ id: string; isOpen: boolean }>) {
  // `render` controls DOM presence; `entered` drives the open/closed row track.
  // Both start in the destination state so an initially-open item doesn't animate.
  const [render, setRender] = useState<boolean>(isOpen);
  const [entered, setEntered] = useState<boolean>(isOpen);

  useEffect(() => {
    if (isOpen) {
      // Mount first; the 0fr->1fr flip happens below once the collapsed frame
      // has painted, otherwise it snaps open.
      setRender(true);
      return;
    }
    setEntered(false);
    const timer = setTimeout(() => setRender(false), ACCORDION_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    // Runs after the 0fr frame has painted, so the flip to 1fr transitions.
    if (render && isOpen) {
      const frame = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(frame);
    }
  }, [render, isOpen]);

  if (!render) {
    return null;
  }

  return (
    <div
      id={id}
      style={{ transitionDuration: `${ACCORDION_TRANSITION_MS}ms` }}
      className={cn(
        'grid py-2 transition-[grid-template-rows,padding] ease-out motion-reduce:transition-none',
        entered ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] py-0',
      )}
    >
      <div className="overflow-hidden">
        <div className="flex w-full flex-col gap-3 px-4 pt-1 pb-2">{children}</div>
      </div>
    </div>
  );
}

// Collapsed-row placeholder that mirrors the accordion header shape (leading icon, title bar,
// trailing chevron, dividers between rows) so loading states match the rendered accordion rather
// than a generic block. Widths are staggered for a natural look.
const SKELETON_TITLE_WIDTHS = ['w-40', 'w-32', 'w-48', 'w-36'] as const;

export function AccordionSkeleton({ count = 3 }: { count?: number }) {
  const rows = Array.from({ length: Math.max(count, 1) }, (_, i) => i);

  return (
    <div
      className="flex w-full flex-col"
      role="progressbar"
      aria-busy
      aria-label={t('aria:loading')}
    >
      {rows.flatMap((row, index) => [
        index > 0 && <Divider key={`accordion-skeleton-divider-${row}`} />,
        <div key={`accordion-skeleton-${row}`} className="flex w-full flex-col">
          <span className="flex w-full items-center justify-between gap-3 px-4 py-3">
            <span className="flex items-center gap-1.5">
              <span className={cn('size-5 rounded-full', SHIMMER_CLASSES)} />
              <span
                className={cn(
                  'h-4 rounded-full',
                  SKELETON_TITLE_WIDTHS[index % SKELETON_TITLE_WIDTHS.length],
                  SHIMMER_CLASSES,
                )}
              />
            </span>
            <span className={cn('size-5 rounded-full', SHIMMER_CLASSES)} />
          </span>
        </div>,
      ])}
    </div>
  );
}
